// frontend/src/components/UnifiedSearchBar.tsx - Fixed search icon size
import React, { useState, useCallback } from "react";
import { debounce } from "../utils/debounce";

interface UnifiedSearchBarProps {
  onSearch: (query: string, difficulty?: string) => void;
  onMetaFilter: () => void;
  isLoading: boolean;
}

export const UnifiedSearchBar: React.FC<UnifiedSearchBarProps> = ({
  onSearch,
  onMetaFilter,
  isLoading,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  // Don't default difficulty - let backend determine appropriate difficulty based on topic
  const [selectedDifficulty, setSelectedDifficulty] = useState<string | undefined>(undefined);

  // Debounce search to avoid too many API calls
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((term: string, difficulty: string) => {
      onSearch(term, difficulty);
    }, 300),
    [onSearch]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    // Removed automatic search - only search when button is clicked
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleDifficultyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const difficulty = e.target.value;
    setSelectedDifficulty(difficulty);
    // Removed automatic search - only search when button is clicked
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(searchTerm, selectedDifficulty);
  };

  const handleClear = () => {
    setSearchTerm("");
    onSearch("", selectedDifficulty);
  };

  return (
    <div className="unified-search-container">
      <form onSubmit={handleSubmit} className="search-form">
        <div className="search-input-container">
          <div className="search-icon">
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          <input
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            placeholder="Search problems or describe what you want to practice..."
            className="search-input"
            disabled={isLoading}
          />

          {searchTerm && (
            <button
              type="button"
              onClick={handleClear}
              className="clear-button"
              disabled={isLoading}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}


          <button
            type="submit"
            className="search-button"
            disabled={isLoading || !searchTerm.trim()}
          >
            {isLoading ? (
              <div className="loading-spinner">
                <svg
                  className="w-4 h-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
            ) : (
              "Search"
            )}
          </button>
        </div>
      </form>

      <div className="quick-actions">
        <button
          type="button"
          onClick={onMetaFilter}
          className="meta-filter-button"
          disabled={isLoading}
        >
          <svg className="meta-logo w-5 h-5" viewBox="0 0 24 24" fill="none">
            <path d="M7.2 6C4.5 6 2.4 8.1 2.4 10.8c0 1.8.9 3.4 2.3 4.4 1.4 1 3.3 1.2 4.9.6.8-.3 1.5-.8 2-1.5L12 13.2l.4 1.1c.5.7 1.2 1.2 2 1.5 1.6.6 3.5.4 4.9-.6 1.4-1 2.3-2.6 2.3-4.4 0-2.7-2.1-4.8-4.8-4.8-1.5 0-2.9.7-3.8 1.9L12 9l-1-.1C10.1 6.7 8.7 6 7.2 6zm0 2c.9 0 1.7.4 2.2 1.1L12 12l2.6-2.9C15.1 8.4 15.9 8 16.8 8c1.5 0 2.8 1.3 2.8 2.8 0 1-.5 1.9-1.3 2.5-.8.6-1.9.7-2.9.3-.5-.2-.9-.5-1.2-.9L12 9.8l-2.2 2.9c-.3.4-.7.7-1.2.9-1 .4-2.1.3-2.9-.3-.8-.6-1.3-1.5-1.3-2.5C4.4 9.3 5.7 8 7.2 8z" fill="#0866ff"/>
          </svg>
          <span>Meta</span>
        </button>

        <div className="search-tips">
          <span className="tips-icon">💡</span>
          <span className="tips-text">
            Try: "leetcode 119", "binary search", "two sum" or "meta"
          </span>
        </div>
      </div>
    </div>
  );
};
